import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, FormControl } from '@angular/forms';
import { DataService } from 'src/app/services/data.service';
import { Router } from '@angular/router';
import { EncryptionService } from 'src/app/services/encryption.service';
import Swal from "sweetalert2";
import { environment } from 'src/environments/environment';

declare global {
  interface Window {
    google?: any;
  }
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {

  authForm!: FormGroup;
  step: 1 | 2 | 3 = 1;
  submitted = false;
  loading = false;
  otpInvalid = false;

  otpControls = Array.from({ length: 6 }, () => new FormControl(''));
  timer = '05:00';
  canResend = true;
  private timerInterval: any;
  public organizerOld: boolean = false;

  constructor(
    private fb: FormBuilder,
    private _ds: DataService,
    private router: Router,
    private enc: EncryptionService
  ) { }

  ngOnInit(): void {
    this.authForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      accounttype: ['', Validators.required]
    });
  }

  loginWithGoogle() {
    this.submitted = true;
    if (this.authForm.get('accounttype')?.invalid) {
      Swal.fire('Please select account type first');
      return;
    }

    if (!environment.googleClientId || environment.googleClientId.includes('REPLACE_ME')) {
      Swal.fire('Google Client ID missing. Please set environment.googleClientId');
      return;
    }

    if (!window.google?.accounts?.id) {
      Swal.fire('Google sign-in library not loaded. Please check `src/index.html` script include.');
      return;
    }

    this.loading = true;

    window.google.accounts.id.initialize({
      client_id: environment.googleClientId,
      callback: (resp: any) => {
        const idToken = resp?.credential;
        if (!idToken) {
          this.loading = false;
          Swal.fire('Google sign-in failed. No credential received.');
          return;
        }

        // Backend exchange: Google ID token -> app token + encrypted user payload
        this._ds.googleLogin({ id_token: idToken, accounttype: this.authForm.value.accounttype }).subscribe({
          next: (res: any) => {
            try {
              // Keep same shape as OTP flow
              const decryptedData = res?.data ? this.enc.decrypt(res.data) : res?.user || res;
              if (decryptedData) localStorage.setItem('user', JSON.stringify(decryptedData));
              if (res?.token) localStorage.setItem('token', res.token);
              const organizerPayload = res?.organizer || decryptedData?.organizer;
              if (organizerPayload) localStorage.setItem('organizer', JSON.stringify(organizerPayload));
              this._ds.getDiorToken();

              this.organizerOld = !!JSON.parse(localStorage.getItem('user') || '{}')?.organizer_exists;
              this.step = 3;
            } catch (e) {
              Swal.fire('Login succeeded but response could not be processed.');
            } finally {
              this.loading = false;
            }
          },
          error: (err: any) => {
            this.loading = false;
            Swal.fire(err?.error?.message || 'Google login failed');
          }
        });
      }
    });

    // Opens the Google One Tap / prompt; if browser blocks it, you can switch to renderButton flow later
    window.google.accounts.id.prompt((notification: any) => {
      if (notification?.isNotDisplayed?.() || notification?.isSkippedMoment?.()) {
        this.loading = false;
      }
    });
  }

  get email(): AbstractControl | null {
    return this.authForm.get('email');
  }

  onSubmit() {
    if (this.step === 1) {
      this.sendOtp();
    }
  }

  sendOtp() {
    this.submitted = true;
    if (this.authForm.invalid) return;

    this.loading = true;

    const payload = { email: this.email?.value };

    this._ds.sendEmail(payload).subscribe({
      next: () => {
        this.loading = false;
        this.step = 2;
        this.startTimer(120);
      },
      error: (err) => {
        this.loading = false;
        Swal.fire(err?.error?.message || 'Failed to send OTP');
      }
    });
  }

  verifyOtp() {
    if (!this.isOtpComplete()) return;

    this.loading = true;
    const otp = this.otpControls.map(c => c.value).join('');

    const payload = {
      email: this.email?.value,
      otp: otp
    };

    this._ds.verifyOTP(payload).subscribe({
      next: (res) => {
        const decryptedData = this.enc.decrypt(res.data);
        localStorage.setItem('user', JSON.stringify(decryptedData));
        // localStorage.setItem('token', JSON.stringify(decryptedData));
        if (res?.token) localStorage.setItem('token', res.token);
        this._ds.getDiorToken();

        this.loading = false;
        const decryptedData2 = JSON.parse(localStorage.getItem('user') || '{}');
        this.organizerOld = decryptedData2.organizer_exists;
        console.log(this.organizerOld)
        this.step = 3;

        // if (!decryptedData.organizer_exists) {
        //   this.router.navigate(['/' + this.authForm.value.accounttype]);
        // } else if (!decryptedData.bank_details_exists) {
        //   this.router.navigate(['/platform-legal-organizer']);
        // } else if (!decryptedData.documents.gst_exists) {
        //   this.router.navigate(['/gst-registration']);
        // } else if (!decryptedData.documents.pan_exists) {
        //   this.router.navigate(['/platform-legal']);
        // } else {
        //   this.router.navigate(['/success']);
        // }
      },
      error: () => {
        this.loading = false;
        this.otpInvalid = true;
        setTimeout(() => this.otpInvalid = false, 3000);
      }
    });
  }

  continueToProfile() {
    const decryptedData = JSON.parse(localStorage.getItem('user') || '{}');
    const decryptedData3 = JSON.parse(localStorage.getItem('organizer') || '{}');

    const organizerExists = decryptedData.organizer_exists;
    // this.organizerOld = decryptedData.organizer_exists;
    // console.log(this.organizerOld)
    const bankExists = decryptedData.bank_details_exists;
    const gstExists = decryptedData.documents?.gst_exists;
    const eventInfo = decryptedData.events_category_exists
    const panExists = decryptedData.documents?.pan_exists;
    const agreementExists = decryptedData.organizer?.agreement_download;
    const hasGST = decryptedData.organizer?.has_gst;
    const mainID = decryptedData.organizer?.id



    // 🔥 Special case first (highest priority)
    // if (organizerExists && !bankExists && !gstExists && !panExists) {
    //   if (!eventInfo) {
    //     this.router.navigate(['/event-information']);
    //   } else {
    //     this.router.navigate(['/gst-registration']);
    //   }

    // } else if (!organizerExists) {
    //   this.router.navigate(['/' + this.authForm.value.accounttype]);

    // } else if (!gstExists) {
    //   if (hasGST == 0) {
    //     this.router.navigate(['/gst-registration']);
    //   } else {
    //     this.router.navigate(['/gst-registration'], {queryParams: {id: mainID}});
    //   }
    // } else if (!panExists) {
    //   this.router.navigate(['/platform-legal']);

    // } else if (!bankExists) {
    //   this.router.navigate(['/platform-legal-organizer']);

    // } else if (!agreementExists) {
    //   this.router.navigate(['/agreement']);

    // } else {
    //   this.router.navigate(['/success']);
    // }
    // 1️⃣ Organizer check
    if (!organizerExists) {
      this.router.navigate(['/' + this.authForm.value.accounttype]);
      return;
    }

    // 2️⃣ Event Info check
    if (!eventInfo) {
      this.router.navigate(['/event-information'], {queryParams : {id : decryptedData3.id}});
      return;
    }

    // 3️⃣ GST check
    if (!gstExists) {

      if (hasGST == 0) {
        this.router.navigate(['/gst-registration'], {queryParams : {id : decryptedData3.id}});
        return;
      }
    }

    // 4️⃣ PAN check
    if (!panExists) {
      this.router.navigate(['/platform-legal'], {queryParams : {id : decryptedData3.id}});
      return;
    }

    // 5️⃣ Bank check
    if (!bankExists) {
      this.router.navigate(['/platform-legal-organizer'], {queryParams : {id : decryptedData3.id}});
      return;
    }

    // 6️⃣ Agreement check
    if (agreementExists == 0) {
      this.router.navigate(['/agreement'], {queryParams : {id : decryptedData3.id}});
      return;
    }

    // 7️⃣ Sab complete
    this.router.navigate(['/success']);
  }

  selectType(type: string) {
    this.authForm.patchValue({ accounttype: type });
  }

  maskTimers: any[] = [];

  onOtpInput(event: any, index: number) {
    const inputEl = event.target as HTMLInputElement;
    const value = inputEl.value.replace(/[^0-9]/g, '');

    this.otpControls[index].setValue(value);
    inputEl.value = value;

    // Clear previous timer if user retypes
    if (this.maskTimers[index]) {
      clearTimeout(this.maskTimers[index]);
    }

    // ⏳ 700ms baad mask kar do
    this.maskTimers[index] = setTimeout(() => {
      inputEl.type = 'password';
    }, 700);

    // Type karte hi number dikhe
    inputEl.type = 'text';

    // Next input pe focus
    if (value && index < this.otpControls.length - 1) {
      const next = document.querySelectorAll('.otp-box')[index + 1] as HTMLInputElement;
      next?.focus();
    }
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    const inputEl = event.target as HTMLInputElement;

    if (event.key === 'Backspace') {
      inputEl.type = 'text'; // delete karte waqt number dikhe

      if (!this.otpControls[index].value && index > 0) {
        const prev = document.querySelectorAll('.otp-box')[index - 1] as HTMLInputElement;
        prev?.focus();
        prev.type = 'text';
      }
    }
  }

  onPaste(event: ClipboardEvent) {
    const data = event.clipboardData?.getData('text') || '';
    if (!/^\d+$/.test(data)) return;

    data.slice(0, 6).split('').forEach((d, i) => {
      this.otpControls[i].setValue(d);

      const el = document.querySelectorAll('.otp-box')[i] as HTMLInputElement;
      if (el) {
        el.value = d;
        el.type = 'text';

        setTimeout(() => {
          el.type = 'password';
        }, 700);
      }
    });
  }

  isOtpComplete() {
    return this.otpControls.every(c => c.value?.toString().length === 1);
  }

  resendOtp() {
    if (!this.canResend) return;

    this.canResend = false;   // hide resend
    this.startTimer(300);    // 2 min timer

    // 🔥 Call your resend API
    const payload = { email: this.email?.value };
    this._ds.sendEmail(payload).subscribe({
      next: () => {
        console.log('OTP resent');
      },
      error: (err) => {
        console.error('Resend failed', err);
      }
    });
  }

  startTimer(seconds: number) {
    clearInterval(this.timerInterval);

    let remaining = seconds;
    this.timer = this.formatTime(remaining);

    this.timerInterval = setInterval(() => {
      remaining--;
      this.timer = this.formatTime(remaining);

      if (remaining <= 0) {
        clearInterval(this.timerInterval);
        this.canResend = true;   // 👈 timer khatam → show resend again
      }
    }, 1000);
  }

  formatTime(sec: number): string {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  goBack() {
    this.step = 1;

    // OTP boxes clear
    this.otpControls.forEach(c => c.setValue(''));

    // Optional: OTP error state reset
    this.otpInvalid = false;
  }
}